import type { RosterSummary } from '../components/dossier/RosterSummaryCard';
import axios from 'axios';
import { getApiBaseUrl } from './api';
import { logout } from './authService';
import type { Project, Ticket, ActivityEvent, WorkspaceContext, PlannerEvent, GitHubRepo, PullRequestInfo } from '../types/agents';

// getApiBaseUrl() returns "http://localhost:8000/api" in dev
// Backend route prefix is "/api/agents", so we need "/agents" suffix
const getBase = () => `${getApiBaseUrl()}/agents`;

export const agentsApi = {
  // -- Projects ---------------------------------------------------------------
  listProjects: (params?: { status?: string; limit?: number; clientId?: string }) =>
    axios.get<{ content: { text: string }[] }>(`${getBase()}/projects`, { params }),

  // Delete a project on builder. Without force, refuses delete if the project
  // has committed work and returns 409 with row counts. The caller decides
  // whether to retry with force=true after user confirmation.
  deleteProject: (projectId: string, force: boolean = false) =>
    axios.delete(`${getBase()}/projects/${projectId}`, { params: force ? { force: true } : undefined }),

  getProject: (id: string) =>
    axios.get(`${getBase()}/projects/${id}`),

  getWorkspaceContext: (id: string) =>
    axios.get(`${getBase()}/projects/${id}/context`),

  getProjectOverview: (id: string) =>
    axios.get(`${getBase()}/projects/${id}/overview`),

  getAppStatus: (id: string) =>
    axios.get(`${getBase()}/projects/${id}/app-status`),

  getActivityTimeline: (id: string, params?: { since?: string; ticketId?: string; limit?: number }) =>
    axios.get(`${getBase()}/projects/${id}/activity`, { params }),

  // "How your agents work" dashboard stats (aggregated on validx, proxied).
  getBuildStats: (id: string) =>
    axios.get(`${getBase()}/projects/${id}/build-stats`),

  // Client-resolved variant — backend finds the client's primary validx
  // project. Used on /dashboard so it doesn't depend on the projects list.
  getClientBuildStats: (clientId?: string) =>
    axios.get(`${getBase()}/build-stats`, { params: clientId ? { client_id: clientId } : undefined }),

  // Project cost roll-up (LLM per-feature + sandbox compute + total) —
  // billed to the account that owns the agent. Aggregated on validx, proxied.
  getBuildCosts: (id: string) =>
    axios.get(`${getBase()}/projects/${id}/costs`),

  // Client-resolved variant (same project resolution as getClientBuildStats).
  getClientBuildCosts: (clientId?: string) =>
    axios.get(`${getBase()}/costs`, { params: clientId ? { client_id: clientId } : undefined }),

  readFile: (projectId: string, filePath: string, params?: { startLine?: number; endLine?: number }) =>
    axios.get(`${getBase()}/projects/${projectId}/files/${filePath}`, { params }),

  searchCode: (projectId: string, query: string, glob?: string) =>
    axios.get(`${getBase()}/projects/${projectId}/search`, { params: { q: query, glob } }),

  // -- Tickets ----------------------------------------------------------------
  listTickets: (projectId: string, status?: string) =>
    axios.get(`${getBase()}/projects/${projectId}/tickets`, { params: status ? { status } : undefined }),

  getTicket: (ticketId: string) =>
    axios.get(`${getBase()}/tickets/${ticketId}`),

  createTicket: (data: {
    projectId: string;
    title: string;
    description: string;
    type: string;
    priority?: string;
    plan?: string;
    affectedFiles?: string[];
    acceptanceCriteria?: string;
    requiresHumanApproval?: boolean;
  }) => axios.post(`${getBase()}/tickets`, data),

  submitFeedback: (ticketId: string, feedback: string, approve: boolean = true) =>
    axios.post(`${getBase()}/tickets/${ticketId}/feedback`, { feedback, approve }),

  triggerExecution: (ticketId: string) =>
    axios.post(`${getBase()}/tickets/${ticketId}/execute`),

  // -- Planner ----------------------------------------------------------------
  getPlannerMessages: (projectId: string, cursor?: string, limit?: number) =>
    axios.get<{ messages: Array<{ role: 'user' | 'assistant'; content: string; createdAt: string }>; conversationId: string | null; status: string | null; processing: boolean; hasMore: boolean; nextCursor: string | null }>(
      `${getBase()}/planner/messages`, { params: { projectId, ...(cursor ? { cursor } : {}), ...(limit ? { limit } : {}) } }
    ),

  // -- Connection Status (Composio OAuth) -------------------------------------
  checkConnectionStatus: (provider: string, composioClientId?: string) =>
    axios.get<{ connected: boolean; provider: string; toolkit: string | null }>(
      `${getBase()}/planner/connection-status`,
      { params: { provider, ...(composioClientId ? { composioClientId } : {}) } }
    ),

  // -- Container Management ---------------------------------------------------
  getContainerStatus: (projectId: string) =>
    axios.get(`${getBase()}/projects/${projectId}/container`),

  manageContainer: (projectId: string, action: 'pause' | 'resume' | 'stop') =>
    axios.post(`${getBase()}/projects/${projectId}/container`, { action }),

  // -- Repo Agents (GitHub template agents) ------------------------------------
  getRepoAgent: (id: string) =>
    axios.get(`${getBase()}/repo-agents/${id}`),

  activateRepoAgent: (id: string, validxProjectId: string) =>
    axios.post(`${getBase()}/repo-agents/${id}/activate`, { validxProjectId }),

  // -- Sync Repos (GitHub) ----------------------------------------------------
  listGithubRepos: (clientId: string) =>
    axios.get<{ repos: GitHubRepo[]; total: number }>(`${getBase()}/github-repos`, { params: { clientId } }),

  syncRepos: (clientId: string, repos: GitHubRepo[]) =>
    axios.post<{ synced: number; total: number }>(`${getBase()}/sync-repos`, { clientId, repos }),

  removeRepos: (clientId: string, githubIds: number[]) =>
    axios.post<{ removed: number; total: number }>(`${getBase()}/remove-repos`, { clientId, githubIds }),

  // -- Pull Requests -----------------------------------------------------------
  getPullRequest: (projectId: string) =>
    axios.get<{ _meta?: { pullRequests: PullRequestInfo[] }; content?: { text: string }[] }>(
      `${getBase()}/projects/${projectId}/pr`,
    ),

  acceptPullRequest: (projectId: string, prId?: string) =>
    axios.post(`${getBase()}/projects/${projectId}/pr/accept`, { action: 'merge', prId }),

  rejectPullRequest: (projectId: string, prId?: string) =>
    axios.post(`${getBase()}/projects/${projectId}/pr/reject`, { action: 'close', prId }),

  /** Get the proxied URL for a screenshot attachment */
  getAttachmentUrl: (attachmentId: string) =>
    `${getBase()}/attachments/${attachmentId}`,

  // -- Team / Workspace members / Agent transfer ------------------------------
  listWorkspaceMembers: (clientId: string) =>
    axios.get<{ members: Array<{ id: string; email: string; name: string | null; role: string; userId: string | null; joinedAt: string; lastSeenAt: string | null }> }>(
      `${getBase()}/workspace-members`, { params: { clientId } }
    ),

  transferAgent: (projectId: string, memberId: string) =>
    axios.post<{ success: boolean; projectId: string; newOwner: { id: string; email: string; role: string } }>(
      `${getBase()}/transfer`, { project_id: projectId, member_id: memberId }
    ),

  // -- Workspace team CRUD (proxied to builder via sync service) --------------
  getWorkspace: (workspaceId: string) =>
    axios.get<{ id: string; name: string; email: string; can_invite?: boolean; can_manage?: boolean; can_view_member_costs?: boolean; is_provider?: boolean; can_manage_roles?: boolean; assignable_roles?: Array<{ value: string; label: string }>; departments?: Array<{ id: string; name: string }>; members: Array<{ id: string; email: string; name: string | null; phone?: string | null; role: string; department?: string | null; department_id?: string | null; is_founder?: boolean; is_consultant?: boolean; is_platform_team?: boolean }> }>(
      `${getApiBaseUrl()}/workspace/${workspaceId}`
    ),

  getWorkspaceMemberUsage: (workspaceId: string, params?: { since?: string; until?: string }) =>
    axios.get<{
      since: string;
      until: string;
      can_view_costs: boolean;
      assignments_available: boolean;
      assignments_stale: boolean;
      assignments_synced_at: string | null;
      delivery_available: boolean;
      complete: boolean;
      delivery_team: Array<{
        relationship_id: string;
        kind: 'expert';
        relationship_state: 'current' | 'past';
        expert_id: string | null;
        actor_user_id: string | null;
        name: string;
        email: string | null;
        expert_status: string | null;
        assignments: Array<{
          project_id: string;
          project_name: string;
          project_status: string;
          is_current: boolean;
          assigned_at: string | null;
        }>;
        usage: {
          calls: number;
          total_tokens: number;
          usage_value_usd: number;
          incurred_cost_usd: number;
          customer_charge_usd: number;
          last_used_at: string | null;
          breakdown: Array<{
            surface: string;
            calls: number;
            total_tokens: number;
            usage_value_usd: number;
            incurred_cost_usd: number;
            customer_charge_usd: number;
          }>;
        } | null;
      }>;
      members: Array<{
        member_email: string | null;
        member_name: string | null;
        user_id: string | null;
        kind: 'member' | 'company_automation' | 'unattributed_delivery';
        calls: number;
        total_tokens: number;
        usage_value_usd: number;
        incurred_cost_usd: number;
        customer_charge_usd: number;
        last_used_at: string | null;
        breakdown: Array<{
          surface: string;
          calls: number;
          total_tokens: number;
          usage_value_usd: number;
          incurred_cost_usd: number;
          customer_charge_usd: number;
        }>;
      }>;
    }>(`${getApiBaseUrl()}/workspace/${workspaceId}/member-usage`, { params }),

  addWorkspaceMember: (workspaceId: string, body: { email: string; name?: string; role: string; phone?: string; send_email?: boolean; department_id?: string; job_title?: string; job_description?: string; context?: Record<string, unknown> }) =>
    axios.post(`${getApiBaseUrl()}/workspace/${workspaceId}/members`, body),

  // Invite many at once (e.g. a parsed CSV). Each row is a full member payload
  // and may carry its own context. Returns per-row results.
  addWorkspaceMembersBatch: (workspaceId: string, members: Array<{ email: string; name?: string; role?: string; department_id?: string; send_email?: boolean; job_title?: string; job_description?: string; context?: Record<string, unknown> }>) =>
    axios.post<{ success: boolean; invited: number; total: number; results: Array<{ email: string; status: string; detail?: string; prefill?: string[] }> }>(
      `${getApiBaseUrl()}/workspace/${workspaceId}/members/batch`, { members }),

  // Upload a RAW CSV with any columns — the server detects email/name/role/
  // department and turns the rest into onboarding-prefill context. Departments
  // are created by name as needed. Returns per-row results + skipped rows.
  uploadWorkspaceMembersCsv: (workspaceId: string, csv: string, opts?: { default_role?: string; send_email?: boolean }) =>
    axios.post<{ success: boolean; invited: number; rows: number; skipped: Array<{ line: number; reason: string }>; results: Array<{ email: string; status: string; detail?: string; prefill?: string[] }>; summary?: RosterSummary }>(
      `${getApiBaseUrl()}/workspace/${workspaceId}/members/upload-csv`,
      { csv, ...(opts || {}) }),

  // Lightweight UI feature flags (any authed user).
  getWorkspaceFeatures: () =>
    axios.get<{ invite_context: boolean }>(`${getApiBaseUrl()}/workspace-features`),

  updateWorkspaceMemberRole: (workspaceId: string, email: string, role: string) =>
    axios.patch(`${getApiBaseUrl()}/workspace/${workspaceId}/members/${encodeURIComponent(email)}`, { role }),

  // Owner override — move a member into a department.
  updateWorkspaceMemberDepartment: (workspaceId: string, email: string, departmentId: string) =>
    axios.patch(`${getApiBaseUrl()}/workspace/${workspaceId}/members/${encodeURIComponent(email)}`, { department_id: departmentId }),

  // Save a member's phone so call surfaces auto-fill it next time.
  setWorkspaceMemberPhone: (workspaceId: string, email: string, phone: string) =>
    axios.patch(`${getApiBaseUrl()}/workspace/${workspaceId}/members/${encodeURIComponent(email)}`, { phone }),

  // Place a member in a department ('' clears the placement).
  setWorkspaceMemberDepartment: (workspaceId: string, email: string, departmentId: string) =>
    axios.patch(`${getApiBaseUrl()}/workspace/${workspaceId}/members/${encodeURIComponent(email)}`, { department_id: departmentId }),

  // Provider lane only: flag/unflag a member as an EXPERT (HITL engineer). Expert
  // is a flag, not a role — the member keeps their role and gains the Builder link.
  setWorkspaceMemberExpert: (workspaceId: string, email: string, isExpert: boolean) =>
    axios.patch(`${getApiBaseUrl()}/workspace/${workspaceId}/members/${encodeURIComponent(email)}/expert`, { is_expert: isExpert }),

  // Provider lane only: flag/unflag an OWNER as a builder ADMIN. Admin is a flag,
  // not a role — a flagged owner crosses into the builder as admin.
  setWorkspaceMemberBuilderAdmin: (workspaceId: string, email: string, isBuilderAdmin: boolean) =>
    axios.patch(`${getApiBaseUrl()}/workspace/${workspaceId}/members/${encodeURIComponent(email)}/builder-admin`, { is_builder_admin: isBuilderAdmin }),

  createWorkspaceDepartment: (workspaceId: string, name: string) =>
    axios.post<{ id?: string }>(
      `${getApiBaseUrl()}/workspace/${workspaceId}/departments`, { name },
    ),

  listWorkspaceDepartments: (workspaceId: string) =>
    axios.get<{ departments: Array<{ id: string; name: string; archived?: boolean; members?: number; leadership?: number; operational?: number; level_unknown?: number }> }>(
      `${getApiBaseUrl()}/workspace/${workspaceId}/departments`
    ),

  removeWorkspaceMember: (workspaceId: string, email: string) =>
    axios.delete(`${getApiBaseUrl()}/workspace/${workspaceId}/members/${encodeURIComponent(email)}`),

  listMyWorkspaces: () =>
    axios.get<{ workspaces: Array<{ id: string; name: string; email: string; website: string | null; display_name: string; is_founder: boolean }> }>(
      `${getApiBaseUrl()}/workspace/my-workspaces`
    ),
};

/**
 * SSE planner stream. Posts to the planner endpoint and reads SSE events.
 * Returns an abort function.
 */
export interface FileAttachment {
  name: string;
  type: string;
  size: number;
  data: string;
}

export function streamPlanner(
  projectId: string | null,
  message: string,
  history: { role: string; content: string }[],
  onEvent: (event: PlannerEvent) => void,
  onError: (error: string) => void,
  onComplete: () => void,
  clientId?: string | null,
  attachments?: FileAttachment[],
): () => void {
  const controller = new AbortController();
  const { signal } = controller;

  const apiUrl = `${getApiBaseUrl()}/agents/planner/stream`;
  const token = localStorage.getItem('auth_token');

  fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      ...(projectId ? { project_id: projectId } : {}),
      ...(clientId ? { client_id: clientId } : {}),
      message,
      history,
      ...(attachments && attachments.length > 0 ? { attachments } : {}),
    }),
    signal,
  })
    .then(async (response) => {
      if (!response.ok) {
        // Handle expired JWT the same way axios interceptor does — log out + redirect
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_id');
          localStorage.removeItem('user_role');
          logout('/login');
          return;
        }
        const text = await response.text();
        onError(`HTTP ${response.status}: ${text}`);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        onError('Could not get response reader');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let completed = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            // event type line -- skip, we use data.type instead
          } else if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6)) as PlannerEvent;
              if (data.type === 'complete') {
                completed = true;
                onComplete();
              } else if (data.type === 'error') {
                completed = true;
                onError(data.message || 'Unknown error');
              } else {
                onEvent(data);
              }
            } catch {
              // Ignore malformed JSON lines
            }
          }
        }
      }

      // Only call onComplete if we didn't already get a complete/error event
      if (!completed) {
        onComplete();
      }
    })
    .catch((err) => {
      if (err.name !== 'AbortError') {
        onError(err.message || 'Stream failed');
      }
    });

  return () => controller.abort();
}
